import { useMutation, useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import { useMemo, useState } from 'react';
import { useCurrentUser } from '../../providers/UserProvider';
import { Text } from '../../components/typography';
import styles from './IncidentSearch.module.scss';
import { Form } from 'react-bootstrap';
import TextWithIcon from '../../components/typography/TextWithIcon';
import { useModal } from '../../providers/ModalProvider';
import { incidentStatus } from '../../constants/strings';
import { keys } from '../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';
import Label from '../../components/action_items/Label';
import { getLabel, incidentStatusTypes } from '../../utilities/incidents';

const incidentQuery = loader('./IncidentSearch.fetch.graphql');
const linkMutation = loader('./IncidentSearch.link.graphql');

export default function IncidentSearch({
  inspectionId,
  onSubmit,
  skip,
  hideTitle = false,
}) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(null);
  const { user, assessmentName, incidentName } = useCurrentUser();
  const [linkInspection] = useMutation(linkMutation);
  const { openConfirmationModal } = useModal();

  const { data: { incidents = [] } = {} } = useQuery(incidentQuery, {
    variables: {
      skip,
      options: {
        filters: [
          {
            field: 'status',
            operator: 'in',
            value: [incidentStatus.investigation, incidentStatus.complete],
          },
        ],
      },
    },
  });

  const incidentList = useMemo(() => {
    if (incidents.length) {
      return searchText?.length
        ? incidents.filter((i) => {
            const searchTextLower = searchText.toLowerCase();

            if (i.externalId) {
              return (
                i.externalId.toLowerCase().includes(searchTextLower) ||
                i.title.toLowerCase().includes(searchTextLower) ||
                i.description.toLowerCase().includes(searchTextLower)
              );
            } else {
              return i.id.toString().includes(searchText);
            }
          })
        : incidents;
    }
    return [];
  }, [incidents, searchText]);

  return (
    <div className={styles.container}>
      {!hideTitle && (
        <TextWithIcon noMargin icon="link" weight="bold">
          {`Link to ${user.company?.incidentName || 'Incident'}`}
        </TextWithIcon>
      )}
      <div className={styles.inputContainer}>
        {!!incidents?.length ? (
          <Form.Control
            className={styles.formInput}
            placeholder="Search by ID"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            color={!!incidents?.length ? 'primary' : 'secondary'}
          />
        ) : (
          <Text size="sm" noMargin>
            No Incidents found in this workspace
          </Text>
        )}

        {!!incidents?.length && !!searchText && (
          <div className={styles.dropdownMenu}>
            {incidentList?.map((i, idx) => (
              <div
                key={`related-incident-${idx}`}
                onClick={() => {
                  openConfirmationModal({
                    title: t(keys.incidents.LINK_HEADER, {
                      variable: assessmentName || t(keys.common.ASSESSMENT),
                      incidentName: incidentName || t(keys.common.INCIDENT),
                    }),
                    variant: 'warning',
                    description: t(keys.incidents.LINK_DESCRIPTION, {
                      variable: assessmentName || t(keys.common.ASSESSMENT),
                      incidentTitle: i?.externalId || i?.title,
                    }),
                    onSubmit: () => {
                      linkInspection({
                        variables: { incidentId: i.id, inspectionId },
                      }).then(() => {
                        onSubmit(i);
                        setSearchText(null);
                      });
                    },
                  });
                }}
                className={styles.dropdownItem}
              >
                <Text hover color="accentPrimary" weight="semiBold" noMargin>
                  {i.externalId || i.id.toString().padStart(4, '0')}
                </Text>
                <Text noMargin truncate>
                  {i.title || i.description}
                </Text>
                <Label
                  name={getLabel(i?.status)}
                  color={incidentStatusTypes[i?.status]?.color}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
